import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MousePointerClick, Keyboard, Check, RotateCcw, ArrowRight, Wand2 } from 'lucide-react';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const STEPS = [
  {
    field: "tone",
    question: "What tone should it have?",
    options: ["Inspiring", "Dramatic", "Funny / light", "Calm / soothing", "Suspenseful"],
    suggested: "Inspiring",
    brief: (v) => `${v.toLowerCase()} tone`,
  },
  {
    field: "aspect_ratio",
    question: "Where will it be watched?",
    options: ["YouTube 16:9", "Reels / Shorts 9:16", "Square 1:1"],
    suggested: "Reels / Shorts 9:16",
    brief: (v) => `framed for ${v}`,
  },
  {
    field: "visual.style",
    question: "Pick a visual style.",
    options: ["Pixar-style 3D", "Anime", "Studio Ghibli", "Photoreal cinematic", "Watercolor storybook"],
    suggested: "Pixar-style 3D",
    brief: (v) => `${v} visuals`,
  },
  {
    field: "length",
    question: "How long should the video be?",
    options: ["Short · up to 1 min", "Medium · 1–5 min", "Long · over 5 min"],
    suggested: "Short · up to 1 min",
    brief: (v) => v.split(" · ")[0].toLowerCase(),
  },
];

const HIGHLIGHTS = [
  {
    icon: MousePointerClick,
    title: "Tap, don't type",
    text: "Every choice is a chip. Pick one and the next question appears.",
  },
  {
    icon: TwinneeLogo,
    title: "Smart suggestions",
    text: "Each question marks a suggested answer, and optional ones fall back to a sensible default.",
  },
  {
    icon: Keyboard,
    title: "Type only when you want",
    text: "Have something specific in mind? Tap “Other” and write your own answer.",
  },
];

export const ClickPromptingSection = () => {
  const [picks, setPicks] = useState({});
  const stepIndex = STEPS.findIndex((s) => !picks[s.field]);
  const done = stepIndex === -1;
  const step = done ? null : STEPS[stepIndex];

  const pick = (value) => setPicks((prev) => ({ ...prev, [step.field]: value }));
  const reset = () => setPicks({});

  const briefParts = STEPS.filter((s) => picks[s.field]).map((s) => s.brief(picks[s.field]));

  return (
    <section id="click-prompting" className="text-white py-24 px-4 sm:px-6 lg:px-8 font-sora relative overflow-hidden">
      <div className="absolute top-1/3 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none z-0" style={{ background: "radial-gradient(circle at center, rgba(208,188,255,0.14) 0%, transparent 65%)", filter: "blur(60px)" }} />
      <div className="absolute bottom-0 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none z-0" style={{ background: "radial-gradient(circle at center, rgba(98,250,227,0.12) 0%, transparent 65%)", filter: "blur(60px)" }} />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <p className="text-xs font-semibold tracking-widest text-[#2DD4BF] uppercase mb-3 font-mono">
            CLICK PROMPTING
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.12]">
            No Prompt Writing.{" "}
            <span className="bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] bg-clip-text text-transparent">
              Just Click.
            </span>
          </h2>
          <p className="mt-5 text-gray-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Most AI tools hand you a blank box and expect a perfect prompt. Twinnee asks a few quick questions
            instead, and you answer each one with a tap. Your clicks become the prompt.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-6 lg:gap-8 items-stretch font-jakarta">
          {/* Interactive question card */}
          <div className="rounded-[24px] border border-white/10 bg-[#0a0d14]/85 backdrop-blur-xl p-6 sm:p-8 shadow-2xl flex flex-col" data-testid="click-prompting-demo">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                {STEPS.map((s, i) => (
                  <span
                    key={s.field}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      picks[s.field] ? "w-8 bg-[#62FAE3]" : i === stepIndex ? "w-8 bg-[#A78BFA]" : "w-4 bg-white/15"
                    }`}
                  />
                ))}
              </div>
              <span className="text-[11px] font-mono text-gray-500">
                {done ? "All set" : `Question ${stepIndex + 1} of ${STEPS.length}`}
              </span>
            </div>

            {done ? (
              <div className="flex-1 flex flex-col items-start justify-center gap-4 py-6">
                <div className="w-12 h-12 rounded-2xl bg-[#62FAE3]/15 border border-[#62FAE3]/40 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#62FAE3] stroke-[2.5]" />
                </div>
                <h3 className="text-2xl font-bold tracking-tight">Your video brief is ready.</h3>
                <p className="text-sm text-gray-400">
                  {STEPS.length} taps, zero words typed. Twinnee takes it from here: script, scenes, voice and video.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-xs bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] text-[#120a28] hover:brightness-110 transition-all active:scale-95"
                  >
                    Try it free <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={reset}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold border border-white/15 text-gray-300 hover:text-white hover:bg-white/[0.05] transition-all cursor-pointer"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Play again
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                <div className="flex items-start gap-3 mb-6">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#2DD4BF] flex items-center justify-center shrink-0">
                    <Wand2 className="w-4 h-4 text-white" />
                  </div>
                  <div className="rounded-2xl rounded-tl-sm bg-white/[0.05] border border-white/10 px-4 py-3">
                    <p className="text-sm sm:text-base text-white">{step.question}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pl-11">
                  {step.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => pick(opt)}
                      className={`px-3.5 py-2 rounded-full text-xs sm:text-[13px] border transition-all duration-200 cursor-pointer active:scale-95 ${
                        opt === step.suggested
                          ? "border-[#2DD4BF]/60 bg-[#2DD4BF]/10 text-[#62FAE3] hover:bg-[#2DD4BF]/20"
                          : "border-white/15 bg-white/[0.03] text-gray-200 hover:border-white/30 hover:bg-white/[0.08]"
                      }`}
                    >
                      {opt}
                      {opt === step.suggested ? (
                        <span className="ml-1.5 text-[9px] uppercase tracking-wide opacity-80">suggested</span>
                      ) : null}
                    </button>
                  ))}
                  <span className="px-3.5 py-2 rounded-full text-xs sm:text-[13px] border border-dashed border-white/15 text-gray-500 inline-flex items-center gap-1.5">
                    <Keyboard className="w-3.5 h-3.5" /> Other…
                  </span>
                </div>

                <p className="mt-auto pt-8 pl-11 text-[11px] text-gray-500 inline-flex items-center gap-1.5">
                  <MousePointerClick className="w-3.5 h-3.5 text-[#A78BFA]" /> Tap an answer to try it
                </p>
              </div>
            )}
          </div>

          {/* Live brief + highlights */}
          <div className="flex flex-col gap-6">
            <div className="rounded-[24px] border border-[#62FAE3]/25 bg-[#0c151a]/90 backdrop-blur-xl p-6 sm:p-7 shadow-2xl shadow-[#62FAE3]/5">
              <p className="text-[11px] font-mono font-semibold tracking-widest text-[#2DD4BF] uppercase mb-3">
                Prompt built from your clicks
              </p>
              <p className="text-sm sm:text-[15px] leading-relaxed text-gray-200 min-h-[72px]">
                {briefParts.length ? (
                  <>
                    A video with{" "}
                    {briefParts.map((part, i) => (
                      <span key={part}>
                        <span className="text-[#62FAE3] font-semibold">{part}</span>
                        {i < briefParts.length - 1 ? ", " : "."}
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="text-gray-500">Your answers will appear here as you tap…</span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
                <div key={title} className="flex items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="w-9 h-9 rounded-xl bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#D0BCFF]" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">{title}</h4>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClickPromptingSection;
